/**
 * EnemyTuningScene — Master-detail editor for per-enemy-type balance tuning.
 * Left panel: clickable enemy list. Right panel: preview + categorized sliders.
 * Replaces the old EnemyScaleScene grid layout.
 */

import Phaser from 'phaser';
import {
  CANVAS_WIDTH, CANVAS_HEIGHT, TILE_SIZE,
  EnemyType, ENEMY_STATS, ENEMY_TEXTURES,
} from '@/config/GameConfig';
import {
  GOD, GOD_PARAMS, GodParam, saveGodMode,
  getEnemyTypeScale, setEnemyTypeScale, resetEnemyTypeScales,
  getEnemyAnchor, setEnemyAnchor, resetEnemyAnchors,
  getEnemyTypeStatOverride, setEnemyTypeStatOverride,
  saveEnemyTypeStatOverrides, resetEnemyTypeStatOverrides,
} from '@/config/GodMode';
import { createTechButton, createTechSlider, TECH, showConfirmDialog } from '@/ui/TechUI';
import { SoundManager } from '@/systems/SoundManager';
import { BASE_POWER, setBasePower, resetBasePower } from '@/systems/WaveComposer';

// ── Layout constants ──
const TOP_H = 52;
const BOT_H = 50;
const LEFT_W = 200;
const LIST_ITEM_H = 42;
const PREVIEW_W = 280;
const PREVIEW_CX = LEFT_W + PREVIEW_W / 2;        // 340
const SLIDER_X = LEFT_W + PREVIEW_W + 20;          // 500
const SLIDER_W = CANVAS_WIDTH - SLIDER_X - 20;     // 680
const WORK_TOP = TOP_H;
const WORK_H = CANVAS_HEIGHT - TOP_H - BOT_H;
const WORK_CY = TOP_H + WORK_H / 2;
const SLIDER_GAP = 30;
const CAT_GAP = 14;

type Selection = { kind: 'global' } | { kind: 'enemy'; eType: EnemyType };

export class EnemyTuningScene extends Phaser.Scene {
  private returnTo = 'TitleScene';

  // Containers
  private listContainer!: Phaser.GameObjects.Container;
  private previewContainer!: Phaser.GameObjects.Container;
  private sliderContainer!: Phaser.GameObjects.Container;

  // Scroll state
  private listScrollY = 0;
  private sliderScrollY = 0;
  private listMaxScroll = 0;
  private sliderMaxScroll = 0;
  private wheelHandler: ((p: Phaser.Input.Pointer, gx: Phaser.GameObjects.GameObject[], dx: number, dy: number) => void) | null = null;
  private listMask: Phaser.GameObjects.Graphics | null = null;
  private sliderMask: Phaser.GameObjects.Graphics | null = null;

  // Selection
  private selection: Selection = { kind: 'global' };
  private listItems: { bg: Phaser.GameObjects.Graphics; sel: Selection }[] = [];

  // Preview elements (for live refresh)
  private rangeGfx: Phaser.GameObjects.Graphics | null = null;
  private previewSprite: Phaser.GameObjects.Image | null = null;

  constructor() { super({ key: 'EnemyTuningScene' }); }

  init(data: { returnTo?: string }): void {
    this.returnTo = data?.returnTo ?? 'TitleScene';
  }

  create(): void {
    this.listScrollY = 0;
    this.sliderScrollY = 0;
    this.listItems = [];

    // ── Background ──
    this.add.graphics().fillStyle(0x0a0a1a, 1).fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // ── Header ──
    const hdr = this.add.graphics();
    hdr.fillStyle(0x111122, 0.95).fillRect(0, 0, CANVAS_WIDTH, TOP_H);
    hdr.lineStyle(1, 0xffaa44, 0.5).lineBetween(0, TOP_H, CANVAS_WIDTH, TOP_H);
    this.add.text(CANVAS_WIDTH / 2, TOP_H / 2, 'ENEMY TUNING', {
      fontSize: '16px', color: '#ffaa44', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);

    // ── Bottom bar ──
    const bot = this.add.graphics();
    bot.fillStyle(0x111122, 0.95).fillRect(0, CANVAS_HEIGHT - BOT_H, CANVAS_WIDTH, BOT_H);
    bot.lineStyle(1, 0xffaa44, 0.5).lineBetween(0, CANVAS_HEIGHT - BOT_H, CANVAS_WIDTH, CANVAS_HEIGHT - BOT_H);

    const botY = CANVAS_HEIGHT - BOT_H / 2;
    createTechButton(this, CANVAS_WIDTH / 2 - 180, botY, 'RESET ALL', {
      onClick: () => showConfirmDialog(this, 'Reset ALL enemy tuning?', () => {
        resetEnemyTypeScales(); resetEnemyAnchors(); resetBasePower(); resetEnemyTypeStatOverrides();
        this.scene.restart({ returnTo: this.returnTo });
      }),
      width: 100, height: 28, fontSize: '10px', color: TECH.DANGER,
    });
    createTechButton(this, CANVAS_WIDTH / 2 - 40, botY, 'RESET CURRENT', {
      onClick: () => this.resetCurrent(),
      width: 120, height: 28, fontSize: '10px', color: TECH.DANGER,
    });
    createTechButton(this, CANVAS_WIDTH / 2 + 100, botY, 'BACK', {
      onClick: () => { SoundManager.play('click'); this.goBack(); },
      width: 80, height: 28, fontSize: '12px', color: TECH.COLOR,
    });

    this.input.keyboard?.on('keydown-ESC', () => { SoundManager.play('click'); this.goBack(); });

    // ── Left panel background ──
    const lpBg = this.add.graphics();
    lpBg.fillStyle(0x0d0d1a, 0.95).fillRect(0, TOP_H, LEFT_W, WORK_H);
    lpBg.lineStyle(1, 0x333355, 0.5).lineBetween(LEFT_W, TOP_H, LEFT_W, CANVAS_HEIGHT - BOT_H);

    // ── Containers ──
    this.listContainer = this.add.container(0, TOP_H);
    this.previewContainer = this.add.container(0, 0);
    this.sliderContainer = this.add.container(SLIDER_X, TOP_H + 16);

    // Build list
    this.buildEnemyList();

    // Masks
    this.listMask = this.make.graphics({});
    this.listMask.fillStyle(0xffffff).fillRect(0, TOP_H, LEFT_W, WORK_H);
    this.listContainer.setMask(this.listMask.createGeometryMask());

    this.sliderMask = this.make.graphics({});
    this.sliderMask.fillStyle(0xffffff).fillRect(SLIDER_X - 10, TOP_H, SLIDER_W + 30, WORK_H);
    this.sliderContainer.setMask(this.sliderMask.createGeometryMask());

    // Scroll
    this.wheelHandler = (_p: Phaser.Input.Pointer, _gx: Phaser.GameObjects.GameObject[], _dx: number, dy: number) => {
      const ptr = this.input.activePointer;
      if (ptr.x < LEFT_W) {
        this.listScrollY = Phaser.Math.Clamp(this.listScrollY + dy * 0.5, 0, this.listMaxScroll);
        this.listContainer.y = TOP_H - this.listScrollY;
      } else if (ptr.x >= SLIDER_X - 10) {
        this.sliderScrollY = Phaser.Math.Clamp(this.sliderScrollY + dy * 0.5, 0, this.sliderMaxScroll);
        this.sliderContainer.y = TOP_H + 16 - this.sliderScrollY;
      }
    };
    this.input.on('wheel', this.wheelHandler);

    // Initial selection
    this.applySelection({ kind: 'global' });
  }

  // ══════════════════════════════════════
  //  LEFT PANEL — Enemy List
  // ══════════════════════════════════════

  private buildEnemyList(): void {
    const entries: { label: string; color: number; texKey?: string; sel: Selection }[] = [
      { label: 'GLOBAL', color: TECH.ACCENT, sel: { kind: 'global' } },
    ];
    for (const eType of Object.values(EnemyType)) {
      const s = ENEMY_STATS[eType];
      if (!s) continue;
      entries.push({ label: (s.label || eType).toUpperCase(), color: s.color, texKey: ENEMY_TEXTURES[eType], sel: { kind: 'enemy', eType } });
    }

    const totalH = entries.length * LIST_ITEM_H;
    this.listMaxScroll = Math.max(0, totalH - WORK_H);

    for (let i = 0; i < entries.length; i++) {
      const e = entries[i];
      const y = i * LIST_ITEM_H;
      const bg = this.add.graphics();
      this.listContainer.add(bg);
      this.listItems.push({ bg, sel: e.sel });

      // Accent bar
      bg.fillStyle(e.color, 0.6).fillRect(0, y, 4, LIST_ITEM_H);

      // Thumbnail
      if (e.texKey) {
        const thumb = this.add.image(28, y + LIST_ITEM_H / 2, e.texKey);
        const maxD = Math.max(thumb.width, thumb.height);
        thumb.setScale(24 / maxD);
        this.listContainer.add(thumb);
      }

      // Label
      const lbl = this.add.text(52, y + LIST_ITEM_H / 2, e.label, {
        fontSize: '9px', color: '#' + e.color.toString(16).padStart(6, '0'),
        fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0, 0.5);
      this.listContainer.add(lbl);

      // Hit area
      const hit = this.add.rectangle(LEFT_W / 2, y + LIST_ITEM_H / 2, LEFT_W, LIST_ITEM_H, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      this.listContainer.add(hit);
      hit.on('pointerdown', () => {
        SoundManager.play('click');
        this.applySelection(e.sel);
      });
    }
  }

  private highlightListItem(): void {
    for (const item of this.listItems) {
      const isSelected =
        (this.selection.kind === 'global' && item.sel.kind === 'global') ||
        (this.selection.kind === 'enemy' && item.sel.kind === 'enemy' && this.selection.eType === item.sel.eType);
      // Redraw just the selection highlight (accent bar is already drawn)
      if (isSelected) {
        const idx = this.listItems.indexOf(item);
        const y = idx * LIST_ITEM_H;
        item.bg.fillStyle(0xffffff, 0.08).fillRect(4, y, LEFT_W - 4, LIST_ITEM_H);
        item.bg.lineStyle(1, TECH.ACCENT, 0.4).strokeRect(4, y, LEFT_W - 4, LIST_ITEM_H);
      }
    }
  }

  // ══════════════════════════════════════
  //  SELECTION
  // ══════════════════════════════════════

  private applySelection(sel: Selection): void {
    this.selection = sel;

    // Clear previous highlights — redraw all list items
    for (const item of this.listItems) {
      const idx = this.listItems.indexOf(item);
      const y = idx * LIST_ITEM_H;
      const e = item.sel;
      const color = e.kind === 'global' ? TECH.ACCENT : ENEMY_STATS[e.eType]?.color ?? 0xcccccc;
      item.bg.clear();
      item.bg.fillStyle(color, 0.6).fillRect(0, y, 4, LIST_ITEM_H);
    }
    this.highlightListItem();

    // Reset slider scroll
    this.sliderScrollY = 0;
    this.sliderContainer.y = TOP_H + 16;

    // Rebuild right panel
    this.previewContainer.removeAll(true);
    this.sliderContainer.removeAll(true);
    this.rangeGfx = null;
    this.previewSprite = null;

    if (sel.kind === 'global') {
      this.buildGlobalPanel();
    } else {
      this.buildEnemyPreview(sel.eType);
      this.buildEnemySliders(sel.eType);
    }
  }

  // ══════════════════════════════════════
  //  GLOBAL PANEL
  // ══════════════════════════════════════

  private buildGlobalPanel(): void {
    // Title in preview area
    this.previewContainer.add(this.add.text(PREVIEW_CX, WORK_CY - 20, 'GLOBAL\nMULTIPLIERS', {
      fontSize: '14px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold', align: 'center',
    }).setOrigin(0.5));

    // Global sliders
    const enemyParams = GOD_PARAMS.filter(p => p.category === 'enemy');
    let y = 0;

    this.sliderContainer.add(this.add.text(0, y, '── GLOBAL ENEMY PARAMS ──', {
      fontSize: '10px', color: '#ff6644', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0, 0.5));
    y += CAT_GAP + 10;

    for (const param of enemyParams) {
      if (param.type === 'slider') {
        const slider = createTechSlider(
          this, 0, y, param.label,
          param.min ?? 0, param.max ?? 5, param.step ?? 0.1,
          GOD[param.key] as number,
          (v) => { (GOD as any)[param.key] = v; saveGodMode(); },
          SLIDER_W - 120,
        );
        this.sliderContainer.add(slider);
        y += SLIDER_GAP;
      }
    }

    this.sliderMaxScroll = Math.max(0, y - WORK_H + 40);
  }

  // ══════════════════════════════════════
  //  ENEMY PREVIEW
  // ══════════════════════════════════════

  private buildEnemyPreview(eType: EnemyType): void {
    const stats = ENEMY_STATS[eType];
    const texKey = ENEMY_TEXTURES[eType];
    const cy = WORK_CY;

    // Tile reference
    this.previewContainer.add(
      this.add.rectangle(PREVIEW_CX, cy, TILE_SIZE, TILE_SIZE, 0x333344, 0.3)
        .setStrokeStyle(1, 0x555577, 0.5),
    );

    // Range circle
    const maxRange = stats.weapons.length > 0 ? Math.max(...stats.weapons.map(w => w.range)) : 0;
    if (maxRange > 0) {
      const ov = getEnemyTypeStatOverride(eType);
      const radius = maxRange * GOD.enemyWeaponRangeMult * ov.rangeMult;
      this.rangeGfx = this.add.graphics();
      this.rangeGfx.lineStyle(1, stats.color, 0.25).strokeCircle(PREVIEW_CX, cy, radius);
      this.rangeGfx.fillStyle(stats.color, 0.04).fillCircle(PREVIEW_CX, cy, radius);
      this.previewContainer.add(this.rangeGfx);
    }

    // Sprite
    const currentScale = getEnemyTypeScale(eType);
    const displaySize = Math.max(50, stats.radius * 8) * GOD.enemyScaleMult * currentScale;
    const sprite = this.add.image(PREVIEW_CX, cy, texKey);
    const maxDim = Math.max(sprite.width, sprite.height);
    sprite.setScale(displaySize / maxDim);
    this.previewContainer.add(sprite);
    this.previewSprite = sprite;

    // Name
    const colorHex = '#' + stats.color.toString(16).padStart(6, '0');
    this.previewContainer.add(this.add.text(PREVIEW_CX, cy - 80, (stats.label || eType).toUpperCase(), {
      fontSize: '12px', color: colorHex, fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    // Stats summary
    const ov = getEnemyTypeStatOverride(eType);
    const rangeLabel = maxRange > 0
      ? `Range: ${Math.round(maxRange * GOD.enemyWeaponRangeMult * ov.rangeMult)}px`
      : 'MELEE ONLY';
    this.previewContainer.add(this.add.text(PREVIEW_CX, cy + 80, [
      `HP: ${stats.hp}  SPD: ${stats.speed}  DMG: ${stats.baseDamage}`,
      rangeLabel,
    ].join('\n'), {
      fontSize: '8px', color: '#8888aa', fontFamily: 'monospace', align: 'center',
    }).setOrigin(0.5, 0));

    // Draggable anchor
    const anchor = getEnemyAnchor(eType);
    const anchorGfx = this.add.graphics();
    const drawAnchor = (ax: number, ay: number) => {
      anchorGfx.clear();
      anchorGfx.lineStyle(1.5, 0xff4444, 0.9);
      anchorGfx.lineBetween(ax - 6, ay, ax + 6, ay);
      anchorGfx.lineBetween(ax, ay - 6, ax, ay + 6);
      anchorGfx.lineStyle(1, 0xff4444, 0.6).strokeCircle(ax, ay, 4);
    };
    const ax = PREVIEW_CX + anchor.ox;
    const ay = cy + anchor.oy;
    drawAnchor(ax, ay);
    this.previewContainer.add(anchorGfx);

    const anchorHit = this.add.rectangle(ax, ay, 16, 16, 0x000000, 0)
      .setInteractive({ useHandCursor: true, draggable: true });
    this.previewContainer.add(anchorHit);
    this.input.setDraggable(anchorHit);
    anchorHit.on('drag', (_p: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      anchorHit.setPosition(dragX, dragY);
      drawAnchor(dragX, dragY);
      setEnemyAnchor(eType, Math.round(dragX - PREVIEW_CX), Math.round(dragY - cy));
    });

    // Anchor label
    this.previewContainer.add(this.add.text(PREVIEW_CX + 60, cy - 78, 'drag to set anchor', {
      fontSize: '7px', color: '#ff4444', fontFamily: 'monospace',
    }).setOrigin(0.5));
  }

  // ══════════════════════════════════════
  //  ENEMY SLIDERS (data-driven)
  // ══════════════════════════════════════

  private buildEnemySliders(eType: EnemyType): void {
    const stats = ENEMY_STATS[eType];
    const hasMelee = stats.baseDamage > 0;
    const hasWeapons = stats.weapons.length > 0;
    const maxRange = hasWeapons ? Math.max(...stats.weapons.map(w => w.range)) : 0;
    const trackW = SLIDER_W - 120;

    let y = 0;

    // ── VISUAL ──
    y = this.addCategoryHeader(y, 'VISUAL', 0xffaa44);
    const scaleSlider = createTechSlider(this, 0, y, 'Scale', 0.2, 3.0, 0.05, getEnemyTypeScale(eType), (v) => {
      setEnemyTypeScale(eType, v);
      if (this.previewSprite) {
        const displaySize = Math.max(50, stats.radius * 8) * GOD.enemyScaleMult * v;
        const maxDim = Math.max(this.previewSprite.width, this.previewSprite.height);
        this.previewSprite.setScale(displaySize / maxDim);
      }
    }, trackW);
    this.sliderContainer.add(scaleSlider);
    y += SLIDER_GAP;

    // ── COMBAT ──
    y = this.addCategoryHeader(y + CAT_GAP, 'COMBAT', 0xff4444);

    if (hasMelee) {
      const ov = getEnemyTypeStatOverride(eType);
      const s = createTechSlider(this, 0, y, `Melee (${stats.baseDamage})`, 0.1, 5.0, 0.05, ov.meleeDamageMult, (v) => {
        const o = getEnemyTypeStatOverride(eType);
        setEnemyTypeStatOverride(eType, { ...o, meleeDamageMult: v });
        saveEnemyTypeStatOverrides();
      }, trackW);
      this.sliderContainer.add(s);
      y += SLIDER_GAP;
    }

    if (hasWeapons) {
      const maxWepDmg = Math.max(...stats.weapons.map(w => w.damage));
      const ov = getEnemyTypeStatOverride(eType);
      const s = createTechSlider(this, 0, y, `Weapon (${maxWepDmg})`, 0.1, 5.0, 0.05, ov.weaponDamageMult, (v) => {
        const o = getEnemyTypeStatOverride(eType);
        setEnemyTypeStatOverride(eType, { ...o, weaponDamageMult: v });
        saveEnemyTypeStatOverrides();
      }, trackW);
      this.sliderContainer.add(s);
      y += SLIDER_GAP;

      // Range
      const rs = createTechSlider(this, 0, y, `Range (${maxRange}px)`, 0.1, 3.0, 0.05, ov.rangeMult, (v) => {
        const o = getEnemyTypeStatOverride(eType);
        setEnemyTypeStatOverride(eType, { ...o, rangeMult: v });
        saveEnemyTypeStatOverrides();
        this.refreshRangeCircle(eType);
      }, trackW);
      this.sliderContainer.add(rs);
      y += SLIDER_GAP;
    }

    // ── HEAL (Healer only) ──
    if (eType === EnemyType.HEALER) {
      const ovH = getEnemyTypeStatOverride(eType);
      const hs = createTechSlider(this, 0, y, `Heal (${GOD.healerAmount})`, 0.1, 5.0, 0.05, ovH.healMult, (v) => {
        const o = getEnemyTypeStatOverride(eType);
        setEnemyTypeStatOverride(eType, { ...o, healMult: v });
        saveEnemyTypeStatOverrides();
      }, trackW);
      this.sliderContainer.add(hs);
      y += SLIDER_GAP;
    }

    if (!hasMelee && !hasWeapons && eType !== EnemyType.HEALER) {
      this.sliderContainer.add(this.add.text(0, y, 'No combat stats', {
        fontSize: '9px', color: '#444455', fontFamily: 'monospace',
      }));
      y += SLIDER_GAP;
    }

    // ── BEHAVIOR ──
    y = this.addCategoryHeader(y + CAT_GAP, 'BEHAVIOR', 0x44ddff);

    const ovSpd = getEnemyTypeStatOverride(eType);
    const spdSlider = createTechSlider(this, 0, y, `Speed (${stats.speed})`, 0.1, 5.0, 0.05, ovSpd.speedMult, (v) => {
      const o = getEnemyTypeStatOverride(eType);
      setEnemyTypeStatOverride(eType, { ...o, speedMult: v });
      saveEnemyTypeStatOverrides();
    }, trackW);
    this.sliderContainer.add(spdSlider);
    y += SLIDER_GAP;

    const currentPower = BASE_POWER[eType] ?? 1;
    const pwSlider = createTechSlider(this, 0, y, 'Power cost', 0.1, 12.0, 0.1, currentPower, (v) => {
      setBasePower(eType, v);
    }, trackW);
    this.sliderContainer.add(pwSlider);
    y += SLIDER_GAP;

    this.sliderMaxScroll = Math.max(0, y - WORK_H + 40);
  }

  private addCategoryHeader(y: number, label: string, color: number): number {
    const colorHex = '#' + color.toString(16).padStart(6, '0');
    this.sliderContainer.add(this.add.text(0, y, `── ${label} ──`, {
      fontSize: '10px', color: colorHex, fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0, 0.5));
    const line = this.add.graphics();
    line.lineStyle(1, color, 0.2).lineBetween(0, y + 8, SLIDER_W - 40, y + 8);
    this.sliderContainer.add(line);
    return y + CAT_GAP + 10;
  }

  private refreshRangeCircle(eType: EnemyType): void {
    if (!this.rangeGfx) return;
    const stats = ENEMY_STATS[eType];
    const maxRange = stats.weapons.length > 0 ? Math.max(...stats.weapons.map(w => w.range)) : 0;
    if (maxRange <= 0) return;
    const ov = getEnemyTypeStatOverride(eType);
    const radius = maxRange * GOD.enemyWeaponRangeMult * ov.rangeMult;
    this.rangeGfx.clear();
    this.rangeGfx.lineStyle(1, stats.color, 0.25).strokeCircle(PREVIEW_CX, WORK_CY, radius);
    this.rangeGfx.fillStyle(stats.color, 0.04).fillCircle(PREVIEW_CX, WORK_CY, radius);
  }

  // ══════════════════════════════════════
  //  RESET / NAV
  // ══════════════════════════════════════

  private resetCurrent(): void {
    if (this.selection.kind === 'global') {
      showConfirmDialog(this, 'Reset global enemy params to 1.0?', () => {
        const enemyParams = GOD_PARAMS.filter(p => p.category === 'enemy');
        for (const p of enemyParams) {
          (GOD as any)[p.key] = p.type === 'slider' ? 1 : true;
        }
        saveGodMode();
        this.applySelection(this.selection);
      });
    } else {
      const eType = this.selection.eType;
      showConfirmDialog(this, `Reset ${eType} tuning?`, () => {
        setEnemyTypeScale(eType, 1);
        setEnemyAnchor(eType, 0, 0);
        setEnemyTypeStatOverride(eType, { hpMult: 1, speedMult: 1, damageMult: 1, meleeDamageMult: 1, weaponDamageMult: 1, rangeMult: 1, healMult: 1 });
        resetBasePower(); // resets all to defaults (no per-type reset available)
        this.applySelection(this.selection);
      });
    }
  }

  private goBack(): void {
    this.cleanup();
    this.scene.start(this.returnTo);
  }

  private cleanup(): void {
    if (this.wheelHandler) { this.input.off('wheel', this.wheelHandler); this.wheelHandler = null; }
    if (this.listMask) { this.listMask.destroy(); this.listMask = null; }
    if (this.sliderMask) { this.sliderMask.destroy(); this.sliderMask = null; }
  }

  shutdown(): void { this.cleanup(); }
}
